import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { clinicService, type ClinicListItem } from '@/services/clinic.service';
import { useAuth } from '@/contexts/AuthContext';
import { hasConsent } from '@/lib/cookieConsent';
import { Outlet } from 'react-router-dom';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ClinicScopeValue {
  /** User's authorized clinics */
  clinics: ClinicListItem[];
  /** Currently selected clinic ID, or null for "All Clinics" (admins only) */
  activeClinicId: string | null;
  /** Update the selected clinic */
  setActiveClinicId: (id: string | null) => void;
  /** True when user has access to multiple clinics */
  isMultiClinic: boolean;
  /** True when user has tenant-wide access (can see all clinics) */
  isTenantWide: boolean;
  /** True while loading clinics */
  isLoading: boolean;
}

const ClinicScopeContext = createContext<ClinicScopeValue | null>(null);

// ── LocalStorage Key ─────────────────────────────────────────────────────────

function getStorageKey(tenantId: string): string {
  return `clinicScope_${tenantId}_activeClinicId`;
}

// ── Provider ─────────────────────────────────────────────────────────────────

export function ClinicScopeProvider({ children }: { children?: ReactNode }) {
  const { tenant, user } = useAuth();
  const tenantId = tenant?.id ?? '';
  const isCentralAdmin = user?.role === 'central_admin';

  const { data: clinics = [], isLoading } = useQuery({
    queryKey: ['clinic', 'clinics'],
    queryFn: () => clinicService.listClinics().then((r) => r.data.data),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Central admins always have tenant-wide access, regardless of clinic count
  const isTenantWide = isCentralAdmin || clinics.length > 1;
  const isMultiClinic = isCentralAdmin || clinics.length > 1;

  // Initialize from localStorage (only if functional consent granted)
  const [activeClinicId, setActiveClinicIdState] = useState<string | null>(() => {
    if (!tenantId) return null;
    if (!hasConsent('functional')) return null;
    return localStorage.getItem(getStorageKey(tenantId));
  });

  // Auto-select when clinics load
  useEffect(() => {
    if (isLoading) return;

    // Central admins default to "All Clinics" (null) when no stored selection
    if (isCentralAdmin && clinics.length === 0) return;

    if (clinics.length === 0) return;

    if (clinics.length === 1 && !isCentralAdmin) {
      // Single clinic user — auto-select, no dropdown
      setActiveClinicIdState(clinics[0]!.id);
    } else if (activeClinicId !== null) {
      // Validate stored selection is still valid
      const isValid = clinics.some((c) => c.id === activeClinicId);
      if (!isValid) {
        // Stored clinic no longer accessible — default to first or "All"
        if (isCentralAdmin) {
          setActiveClinicIdState(null);
          if (tenantId && hasConsent('functional')) localStorage.removeItem(getStorageKey(tenantId));
        } else {
          setActiveClinicIdState(clinics[0]!.id);
          if (tenantId && hasConsent('functional')) localStorage.setItem(getStorageKey(tenantId), clinics[0]!.id);
        }
      }
    } else if (!isCentralAdmin) {
      // No stored selection (multi-clinic non-admin) — auto-select first clinic
      setActiveClinicIdState(clinics[0]!.id);
      if (tenantId && hasConsent('functional')) localStorage.setItem(getStorageKey(tenantId), clinics[0]!.id);
    }
    // Only run when clinics load/change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinics, isLoading]);

  const setActiveClinicId = useCallback(
    (id: string | null) => {
      setActiveClinicIdState(id);
      if (tenantId && hasConsent('functional')) {
        if (id === null) {
          localStorage.removeItem(getStorageKey(tenantId));
        } else {
          localStorage.setItem(getStorageKey(tenantId), id);
        }
      }
    },
    [tenantId],
  );

  const value = useMemo<ClinicScopeValue>(
    () => ({
      clinics,
      activeClinicId,
      setActiveClinicId,
      isMultiClinic,
      isTenantWide,
      isLoading,
    }),
    [clinics, activeClinicId, setActiveClinicId, isMultiClinic, isTenantWide, isLoading],
  );

  return (
    <ClinicScopeContext.Provider value={value}>
      {children ?? <Outlet />}
    </ClinicScopeContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useClinicScope(): ClinicScopeValue {
  const ctx = useContext(ClinicScopeContext);
  if (!ctx) {
    throw new Error('useClinicScope must be used within a ClinicScopeProvider');
  }
  return ctx;
}
